/**
 * Utilitaire pour la compression et le redimensionnement automatique des images
 * Optimise les images pour réduire leur taille tout en conservant une qualité acceptable
 */

interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
}

/**
 * Compresse et redimensionne une image automatiquement
 * @param file Fichier image à traiter
 * @param options Options de compression
 * @returns Promise<string> Image compressée en base64
 */
export async function compressImage(
  file: File, 
  options: CompressionOptions = {}
): Promise<string> {
  const {
    maxWidth = 1200,
    maxHeight = 800,
    quality = 0.8,
    maxSizeKB = 500 // 500KB max au lieu de 5MB
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        // Calculer les nouvelles dimensions en conservant le ratio
        const { width: newWidth, height: newHeight } = calculateDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        // Configurer le canvas
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Dessiner l'image redimensionnée
        ctx?.drawImage(img, 0, 0, newWidth, newHeight);

        // Fonction récursive pour ajuster la qualité si nécessaire
        const tryCompress = (currentQuality: number): void => {
          const dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
          const sizeKB = Math.round((dataUrl.length * 3) / 4 / 1024); // Estimation de la taille

          console.log(`📸 Compression: ${sizeKB}KB avec qualité ${Math.round(currentQuality * 100)}%`);

          // Si la taille est acceptable ou si on a atteint la qualité minimale
          if (sizeKB <= maxSizeKB || currentQuality <= 0.1) {
            resolve(dataUrl);
            return;
          }

          // Réduire la qualité et réessayer
          const newQuality = Math.max(0.1, currentQuality - 0.1);
          tryCompress(newQuality);
        };

        // Commencer la compression
        tryCompress(quality);

      } catch (error) {
        reject(new Error('Erreur lors de la compression de l\'image'));
      }
    };

    img.onerror = () => {
      reject(new Error('Erreur lors du chargement de l\'image'));
    };

    // Charger l'image
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calcule les nouvelles dimensions en conservant le ratio d'aspect
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let { width, height } = { width: originalWidth, height: originalHeight };

  // Redimensionner si nécessaire
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return {
    width: Math.round(width),
    height: Math.round(height)
  };
}

/**
 * Valide qu'un fichier est une image valide
 */
export function validateImageFile(file: File): { isValid: boolean; error?: string } {
  // Vérifier le type MIME
  if (!file.type.startsWith('image/')) {
    return {
      isValid: false,
      error: 'Le fichier doit être une image (JPG, PNG, WebP, etc.)'
    };
  }

  // Vérifier les types supportés
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  if (!supportedTypes.includes(file.type.toLowerCase())) {
    return {
      isValid: false,
      error: 'Format non supporté. Utilisez JPG, PNG, WebP ou GIF.'
    };
  }

  // Vérifier la taille brute (max 20MB avant compression)
  const maxRawSizeMB = 20;
  if (file.size > maxRawSizeMB * 1024 * 1024) {
    return {
      isValid: false,
      error: `L'image est trop volumineuse (${Math.round(file.size / 1024 / 1024)}MB). Maximum ${maxRawSizeMB}MB.`
    };
  }

  return { isValid: true };
}

/**
 * Formate la taille d'un fichier pour l'affichage
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

